// Meddelanden i projektet: trådar riktade till en roll, med krav på åtgärd.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, MessageSquarePlus, CornerDownRight } from "lucide-react";
import useProjectRole from "@/hooks/useProjectRole";
import { PROJECT_ROLES, type ProjectRole } from "@/lib/basProcess/roleViews";

interface MessageRow {
  id: string;
  parent_id: string | null;
  body: string;
  audience_role: string | null;
  context_label: string | null;
  requires_action: boolean | null;
  due_date: string | null;
  resolved_at: string | null;
  author_name: string | null;
  author_role: string | null;
  created_at: string;
}

const ALL = "alla";

const fmt = (iso: string) =>
  new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });

export const ProjectMessages: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { role, canEdit } = useProjectRole(projectId);
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState<string>(ALL);
  const [contextLabel, setContextLabel] = useState("");
  const [requiresAction, setRequiresAction] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("project_messages")
      .select(
        "id, parent_id, body, audience_role, context_label, requires_action, due_date, resolved_at, author_name, author_role, created_at"
      )
      .eq("project_id", projectId)
      .order("created_at", { ascending: true })
      .limit(300);
    if (error) toast.error("Kunde inte hämta meddelanden");
    setMessages((data || []) as MessageRow[]);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async (text: string, parentId: string | null) => {
    if (!text.trim()) {
      toast.error("Skriv ett meddelande först");
      return;
    }
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("project_messages").insert({
      project_id: projectId,
      parent_id: parentId,
      body: text.trim(),
      audience_role: parentId ? null : audience === ALL ? null : audience,
      context_type: "project",
      context_label: parentId ? null : contextLabel.trim() || null,
      requires_action: parentId ? false : requiresAction,
      due_date: parentId || !dueDate ? null : dueDate,
      author_id: auth.user?.id ?? null,
      author_name: auth.user?.email ?? null,
      author_role: role,
    });
    if (error) {
      toast.error("Kunde inte skicka meddelandet");
      return;
    }
    if (parentId) {
      setReplyTo(null);
      setReplyBody("");
    } else {
      setBody("");
      setContextLabel("");
      setRequiresAction(false);
      setDueDate("");
    }
    load();
  };

  const resolve = async (id: string) => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("project_messages")
      .update({ resolved_at: new Date().toISOString(), resolved_by: auth.user?.id ?? null })
      .eq("id", id);
    if (error) toast.error("Kunde inte markera som klar");
    else load();
  };

  const threads = useMemo(() => {
    const roots = messages.filter((m) => !m.parent_id);
    const filtered = onlyMine
      ? roots.filter((m) => !m.audience_role || m.audience_role === role)
      : roots;
    return filtered
      .slice()
      .reverse()
      .map((root) => ({
        root,
        replies: messages.filter((m) => m.parent_id === root.id),
      }));
  }, [messages, onlyMine, role]);

  return (
    <div className="space-y-4">
      {canEdit && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Nytt meddelande</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Vad behöver de andra i projektet veta?"
              rows={3}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className="w-full sm:w-[190px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Till alla i projektet</SelectItem>
                  {PROJECT_ROLES.map((r: ProjectRole) => (
                    <SelectItem key={r} value={r}>
                      Till {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={contextLabel}
                onChange={(e) => setContextLabel(e.target.value)}
                placeholder="Gäller (t.ex. Dok 12 AMP)"
                className="w-full sm:w-[220px]"
              />
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={requiresAction}
                  onCheckedChange={(v) => setRequiresAction(v === true)}
                />
                Kräver åtgärd
              </label>
              {requiresAction && (
                <Input
                  type="date"
                  aria-label="Senast"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full sm:w-[160px]"
                />
              )}
              <Button onClick={() => send(body, null)}>
                <MessageSquarePlus className="mr-1 h-4 w-4" /> Skicka
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2">
        <Button variant={onlyMine ? "default" : "outline"} size="sm" onClick={() => setOnlyMine((v) => !v)}>
          {onlyMine ? `Visar bara ${role}` : "Visa allt"}
        </Button>
        <span className="text-xs text-muted-foreground">{threads.length} trådar</span>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Laddar…</p>}
      {!loading && threads.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Inga meddelanden ännu. Skriv det första så syns det för alla i projektet.
        </p>
      )}

      <div className="space-y-3">
        {threads.map(({ root, replies }) => (
          <Card key={root.id}>
            <CardContent className="space-y-3 pt-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{root.author_role || "Deltagare"}</Badge>
                <span className="text-sm font-medium">{root.author_name || "Okänd"}</span>
                <span className="text-xs text-muted-foreground">{fmt(root.created_at)}</span>
                {root.audience_role && <Badge variant="outline">Till {root.audience_role}</Badge>}
                {root.context_label && <Badge variant="outline">{root.context_label}</Badge>}
                {root.requires_action && !root.resolved_at && (
                  <Badge variant="destructive">
                    Kräver åtgärd{root.due_date ? ` senast ${root.due_date}` : ""}
                  </Badge>
                )}
                {root.resolved_at && <Badge>Klar</Badge>}
              </div>

              <p className="whitespace-pre-wrap text-sm text-foreground">{root.body}</p>

              {replies.length > 0 && (
                <div className="space-y-2 border-l pl-4">
                  {replies.map((r) => (
                    <div key={r.id} className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <CornerDownRight className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm font-medium">{r.author_name || "Okänd"}</span>
                        <Badge variant="secondary">{r.author_role || "Deltagare"}</Badge>
                        <span className="text-xs text-muted-foreground">{fmt(r.created_at)}</span>
                      </div>
                      <p className="whitespace-pre-wrap text-sm text-muted-foreground">{r.body}</p>
                    </div>
                  ))}
                </div>
              )}

              {canEdit && (
                <div className="flex flex-wrap items-center gap-2">
                  {replyTo === root.id ? (
                    <div className="flex w-full flex-col gap-2 sm:flex-row">
                      <Textarea
                        value={replyBody}
                        onChange={(e) => setReplyBody(e.target.value)}
                        rows={2}
                        placeholder="Ditt svar"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => send(replyBody, root.id)}>
                          Svara
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setReplyTo(null)}>
                          Avbryt
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setReplyTo(root.id)}>
                      Svara
                    </Button>
                  )}
                  {root.requires_action && !root.resolved_at && (
                    <Button size="sm" variant="ghost" onClick={() => resolve(root.id)}>
                      <CheckCircle2 className="mr-1 h-4 w-4" /> Markera som klar
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ProjectMessages;
